import React, { useEffect, useRef, useMemo } from 'react';
import { Primer, SuccessScreenType } from '@primer-io/checkout-web';
import type { PaymentMethodType, PrimerCheckout, UniversalCheckoutOptions } from '@primer-io/checkout-web';
import * as translator from '../../../utils/translator';
import { buildPrimerReturnUrl } from '../paymentService';
import type { PrimerInitData, PaymentContext, PaymentCallbacks, UserClickTracking } from '../paymentTypes';
import type { PaymentServiceOverrides } from './AdyenCheckoutForm';
import { MainTracker } from '../../../PixelTrackers/MainTracker';
import { PRIMER_STYLES } from './primerCheckoutStyle';
import type { CheckoutSectionProps, CheckoutStyleVariant } from './checkoutFormProps';
import { usePrimerManualPayment } from './usePrimerManualPayment';
import { showsPaypalDisclaimer } from '../providers/primerHelpers';

interface PrimerCheckoutFormProps extends CheckoutSectionProps {
  primerInitData: PrimerInitData;
  paymentContext: PaymentContext;
  callbacks: PaymentCallbacks;
  tracking?: UserClickTracking;
  serviceOverrides?: PaymentServiceOverrides;
  variant?: CheckoutStyleVariant;
}

// ============================================================================
// Primer Checkout Form Component
// ============================================================================

const PrimerCheckoutForm: React.FC<PrimerCheckoutFormProps> = ({ primerInitData, paymentContext, callbacks, tracking, serviceOverrides, variant = 'default', allowedMethods, onLoadError, onActivate }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const checkoutRef = useRef<PrimerCheckout | null>(null);
  // Each mount waits for the previous instance's teardown (StrictMode double-mount, new clientToken)
  const teardownChainRef = useRef<Promise<void>>(Promise.resolve());

  const onActivateRef = useRef(onActivate);
  onActivateRef.current = onActivate;

  const { sdkHandlers, trackLoadError, paymentMethodTypeRef, hasTokenizedRef, contextRef } = usePrimerManualPayment({
    paymentContext, callbacks, tracking, serviceOverrides, onLoadError, resubmit: () => checkoutRef.current?.submit(),
  });

  const { clientToken, paymentMethods, isSubscription, orderAddresses } = primerInitData;
  const { language, country, sepaPositionVariant } = paymentContext;
  const lang = language.toLowerCase();
  const hasServiceOverrides = !!serviceOverrides;

  // ============================================================================
  // Build Primer Options
  // ============================================================================

  // By content: a new array with the same types must not re-mount Universal Checkout.
  const offeredMethodsKey = (allowedMethods ?? paymentMethods)?.join(',');
  const offeredMethods = useMemo(() => allowedMethods ?? paymentMethods, [offeredMethodsKey]);
  const options = useMemo<Omit<UniversalCheckoutOptions, 'container'>>(() => ({
    paymentHandling: 'MANUAL',
    locale: `${lang}-${(orderAddresses?.country_id || country).toUpperCase()}`,
    style: PRIMER_STYLES[variant],
    ...(offeredMethods?.length ? { allowedPaymentMethods: offeredMethods as PaymentMethodType[] } : {}),
    card: {
      cardholderName: { visible: true, required: true },
      cardNumber: { placeholder: translator.translate(lang, 'payment', 'placeholders/cardNumber') },
      expiryDate: { placeholder: translator.translate(lang, 'payment', 'placeholders/expiryDate') },
      cvv: { placeholder: translator.translate(lang, 'payment', 'placeholders/securityCodeThreeDigits') },
    },
    successScreen: { type: SuccessScreenType.CHECK, title: translator.translate(lang, 'payment', 'success/title') },
    // The payment-link repay flow (serviceOverrides) has no payment_page_callback return path.
    // TODO(primer-spike): decide the repay return URL if a redirect method can't open its popup.
    ...(!hasServiceOverrides && { redirect: { returnUrl: buildPrimerReturnUrl(contextRef.current, isSubscription) } }),

    onPaymentMethodAction: (action, { paymentMethodType }) => {
      if (action !== 'PAYMENT_METHOD_SELECTED' || !paymentMethodType) return;
      paymentMethodTypeRef.current = paymentMethodType;
      MainTracker.track('custom', 'Payment Method Selected', {
        selected_payment_method: paymentMethodType,
        provider: contextRef.current.paymentServiceProvider,
        ...(sepaPositionVariant && { sepa_position_test: sepaPositionVariant }),
      });
      onActivateRef.current?.();
    },
    ...sdkHandlers,
  }), [lang, country, orderAddresses, variant, offeredMethods, isSubscription, hasServiceOverrides, sepaPositionVariant, sdkHandlers]);

  // ============================================================================
  // Initialize Primer
  // ============================================================================

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !clientToken) return;

    let cancelled = false;
    let instance: PrimerCheckout | null = null;

    const mounted = teardownChainRef.current.then(async () => {
      if (cancelled) return;
      try {
        hasTokenizedRef.current = false;
        instance = await Primer.showUniversalCheckout(clientToken, { ...options, container });
        if (cancelled) {
          instance.teardown?.();
          instance = null;
          return;
        }
        checkoutRef.current = instance;
      } catch (error) {
        trackLoadError(error);
      }
    });

    return () => {
      cancelled = true;
      teardownChainRef.current = mounted.then(() => {
        if (checkoutRef.current === instance) checkoutRef.current = null;
        instance?.teardown?.();
      }).catch(() => {});
    };
  }, [clientToken, options, trackLoadError]);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="primer-checkout-container">
      {showsPaypalDisclaimer(callbacks) && (
        <div className="paypal-verification-disclaimer">
          {translator.translate(lang, 'payment', callbacks.isExpressOrder ? 'idVerificationDisclaimerExpress' : 'idVerificationDisclaimer')}
        </div>
      )}
      <div ref={containerRef} id="primer-checkout-container" />
    </div>
  );
};

export default PrimerCheckoutForm;
